<template>
  <AppContent :label="General.NEW_PROCESS">
    <b-loading
      data-cy="LOADING"
      isFullPage
      :active="isLoading || newProcess.automatic_processing_starting"
    />
    <template>
      <TimelineItem
        stepValue="1"
        :statusColor="`${!validatedStep1 ? 'default' : 'primary'}`"
      >
        <template #title>
          <section
            data-cy="PROCESS_DATA"
            class="columns is-full is-align-items-center"
            :class="isMobile ? 'mx-0 mb-0' : ''"
          >
            <h3 class="column timeline-title is-size-5">
              {{ App.ProcessOpeningScreen.PROCESS_DATA }}
            </h3>
            <div class="column is-hidden-touch" />
            <span class="column tag nup-tag">
              <b-icon
                icon="information-outline"
                :type="isContrast ? 'is-black' : 'is-blue-prussian'"
                class="mr-5"
              />
              <span class="has-text-blue-dark">{{
                General.NUP_GENERATED_PROCESSED
              }}</span>
            </span>
          </section>
        </template>
        <ValidationObserver
          ref="processFormObserver"
          class="container-form-section is-desktop mb-15 mt-5"
          tag="section"
        >
          <div class="columns">
            <div class="column">
              <div
                class="is-6 has-text-weight-semibold is-flex is-align-items-center"
              >
                <label>
                  {{ App.ProcessOpeningScreen.PROTOCOL_REFERENCE }}
                  <span class="optional-label ml-5">
                    {{ General.OPTIONAL }}
                  </span>
                </label>
                <b-tooltip
                  multilined
                  role="tooltip"
                  :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                  :size="isMobile ? 'is-medium' : 'is-large'"
                  :position="isMobile ? 'is-top' : 'is-right'"
                >
                  <template v-slot:content>
                    <h6 class="has-text-weight-semibold is-flex">
                      {{ App.MenuMain.HELP }}
                    </h6>
                    <p class="has-text-left">
                      {{ General.TEXTS.DESCRIPTION_EXTENAL_NUP }}
                    </p>
                  </template>
                  <div class="is-flex">
                    <b-icon
                      class="spacing-ml-10"
                      :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                      size="is-small"
                      icon="information"
                    />
                  </div>
                </b-tooltip>
              </div>
              <div>
                <b-input
                  id="process_reference_field"
                  data-cy="PROCESS_EXTERNAL_REFERENCE_INPUT"
                  :value="newProcess.reference_number"
                  :placeholder="General.PLACEHOLDER.REFERENCE_PROCESS"
                  class="input-field"
                  role="input"
                  @blur="updateReference"
                />
              </div>
            </div>
            <div class="column is-align-self-flex-end">
              <span
                v-if="referenceNumberDetails"
                data-cy="PROCESS_EXTERNAL_REFERENCE_DESCRIPTION"
                class="reference-number-tag"
              >
                <b-icon
                  :type="isContrast ? 'is-black' : 'is-primary'"
                  size="is-small"
                  class="mr-5"
                  icon="alert-circle-outline"
                />
                {{ referenceNumberDetails.description }}
              </span>
            </div>
            <div class="column is-hidden-touch" />
          </div>

          <div class="columns">
            <div class="column">
              <div
                class="is-6 has-text-weight-semibold is-flex is-align-items-center"
              >
                <label>
                  {{ App.ProcessOpeningScreen.PROCESS_REFERENCE }}
                  <span class="optional-label ml-5">{{
                    General.OPTIONAL
                  }}</span>
                </label>
                <b-tooltip
                  multilined
                  role="tooltip"
                  :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                  :size="isMobile ? 'is-medium' : 'is-large'"
                  :position="isMobile ? 'is-top' : 'is-right'"
                >
                  <template v-slot:content>
                    <h6 class="has-text-weight-semibold is-flex">
                      {{ App.MenuMain.HELP }}
                    </h6>
                    <p class="has-text-left">
                      {{ General.TEXTS.DESCRIPTION_NUP }}
                    </p>
                  </template>
                  <div class="is-flex">
                    <b-icon
                      class="spacing-ml-10"
                      :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                      size="is-small"
                      icon="information"
                    />
                  </div>
                </b-tooltip>
              </div>
              <div>
                <CustomTagInput
                  v-model="process_reference"
                  data-cy="PROCESS_REFERENCE_NUPS"
                  autocomplete
                  :beforeAdding="beforeAddingProcessReference"
                  :data="listProcess"
                  field="nup_mask"
                  :placeholder="General.PLACEHOLDER.TYPE_IT_INCLUDE"
                  name="processReference"
                  class="is-12"
                  role="combobox"
                  rounded
                  @keypress.native="isValidNup"
                  @input="option => selectProcessReference(option)"
                  @typing="setProcessReference"
                >
                  <template #selected="props">
                    <b-tag
                      v-for="(tag, index) in props.tags"
                      :key="index"
                      rounded
                      closable
                      class="is-clickable"
                      :aria-close-label="
                        `${General.ARIA_LABEL.DELETE_NUP} ${applyMaskNUP(
                          tag.nup
                        )}`
                      "
                      @close="removeProcessReference(props.tags, index)"
                    >
                      <span @click="setDetailReferenceProcess(tag.nup)">{{
                        applyMaskNUP(tag.nup)
                      }}</span>
                    </b-tag>
                  </template>
                  <template slot="empty">
                    <p v-if="!isLoading">
                      {{ General.MESSAGES.NO_NUP_FOUND.toUpperCase() }}
                    </p>
                    <b-loading v-else :isFullPage="false" :active="isLoading" />
                  </template>
                </CustomTagInput>
              </div>
            </div>
            <div class="column p-0">
              <section class="px-10">
                <span
                  v-if="processReferenceSelected.subject"
                  data-cy="PROCESS_REFERENCE_INFO"
                  class="process-reference-tag"
                >
                  <b-icon
                    :type="isContrast ? 'is-black' : 'is-blue-oil'"
                    size="is-small"
                    icon="alert-circle-outline"
                  />
                  <div>
                    <div class="custom-size">
                      <div>
                        <span class="has-text-weight-semibold mr-5"
                          >{{ General.SUBJECT }}:
                        </span>
                        <span>{{ processReferenceSelected.subject }}</span>
                      </div>
                      <span
                        class="is-clickable is-right"
                        @click="closeReferenceProcess"
                      >
                        <b-icon
                          icon="close"
                          :type="isContrast ? 'is-white-dark' : 'is-grey-light'"
                          size="is-small"
                        />
                      </span>
                    </div>
                    <p class="custom-size">
                      <span class="has-text-weight-semibold">
                        {{ General.OPENING_UNIT }}:
                      </span>
                      <span>{{
                        `${
                          processReferenceSelected.origin.entity.abbreviation
                        }/${
                          processReferenceSelected.origin.abbreviation.includes(
                            "/"
                          )
                            ? processReferenceSelected.origin.abbreviation.split(
                                "/"
                              )[1]
                            : processReferenceSelected.origin.abbreviation
                        } - ${processReferenceSelected.origin.description.toLowerCase()}`
                      }}</span>
                    </p>
                    <p
                      v-if="
                        processReferenceSelected.cc &&
                          processReferenceSelected.cc.length
                      "
                      class="custom-size"
                    >
                      <span class="has-text-weight-semibold"
                        >{{ General.INTERESTED }}:
                      </span>
                      <span>
                        {{ getCC(processReferenceSelected.cc) }}
                      </span>
                    </p>
                  </div>
                </span>
              </section>
            </div>
            <div class="column is-hidden-touch" />
          </div>

          <ValidationProvider
            v-slot="{ errors }"
            rules="required"
            class="field"
            tag="div"
          >
            <b-field :type="errors.length ? 'is-refused' : ''">
              <template #label>
                <span class="subtitle is-6 has-text-weight-semibold">
                  {{ General.SUBJECT }}
                </span>
              </template>
              <b-tooltip
                multilined
                role="tooltip"
                class="tooltip-100"
                :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                size="is-large"
                :active="validatedStep2"
                position="is-top"
              >
                <template v-slot:content>
                  <h6 class="has-text-weight-semibold is-flex">
                    {{ App.MenuMain.HELP }}
                  </h6>
                  <p class="has-text-left">
                    {{ blockedFieldsTooltipText }}
                  </p>
                </template>
                <b-autocomplete
                  v-model="processSubject"
                  data-cy="PROCESS_SUBJECT_INPUT"
                  aria-role="subject"
                  role="combobox"
                  :data="subjects"
                  autocomplete
                  field="completeName"
                  name="subject"
                  :placeholder="General.PLACEHOLDER.SUBJECT"
                  :disabled="validatedStep2"
                  keepFirst
                  @typing="getSubjects"
                  @select="option => onSelectedSubject(option)"
                  @input="value => !value && onSelectedSubject({}, !value)"
                >
                  <template slot-scope="props">
                    <div class="media pre-wrap">
                      <div class="media-content">
                        {{ props.option.completeName }}
                      </div>
                    </div>
                  </template>
                  <template slot="empty">
                    <p v-if="!isLoading">
                      {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
                    </p>
                    <b-loading v-else :isFullPage="false" :active="isLoading" />
                  </template>
                </b-autocomplete>
              </b-tooltip>

              <template #message>
                <ErrorField :errors="errors" />
              </template>
            </b-field>
          </ValidationProvider>

          <b-field>
            <template #label>
              <span class="subtitle is-6 has-text-weight-semibold">
                {{ General.FROM }}</span
              >
            </template>
            <div data-cy="FROM_FIELD">
              <b-taginput
                :value="formattedUserCapacity"
                field="showCaseName"
                name="origin"
                autocomplete
                rounded
                aria-role="from"
                :closable="false"
                readonly
                disabled
              />
            </div>
          </b-field>

          <ValidationProvider
            v-slot="{ errors }"
            rules="required"
            class="field"
            tag="div"
          >
            <b-field :type="errors.length ? 'is-refused' : ''">
              <template #label>
                <span class="subtitle is-6 has-text-weight-semibold">
                  {{ General.TO }}</span
                >
              </template>
              <b-tooltip
                multilined
                role="tooltip"
                class="tooltip-100"
                :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                size="is-large"
                :active="validatedStep2 || !completeLoad"
                position="is-top"
              >
                <template v-slot:content>
                  <h6 class="has-text-weight-semibold is-flex">
                    {{ App.MenuMain.HELP }}
                  </h6>
                  <p class="has-text-left">
                    {{ blockedFieldsTooltipText }}
                  </p>
                </template>

                <CustomTagInput
                  id="capacity-to"
                  ref="capacityTo"
                  v-model="capacityTo"
                  data-cy="PROCESS_CAPACITY_TO_INPUT"
                  :beforeAdding="beforeAddingTo"
                  :data="hierarchyCapacities(filterTagTo)"
                  autocomplete
                  field="name"
                  name="capacityTo"
                  :placeholder="General.PLACEHOLDER.SECTOR"
                  rounded
                  :disabled="validatedStep2 || !completeLoad"
                  :hasCounter="false"
                  class="has-tag-ellipsis"
                  role="combobox"
                  @typing="setFilterTagTo"
                  @input="onSelectedToDraftAndLimitToProcessor"
                >
                  <template slot-scope="props">
                    <div class="is-flex">
                      <span class="has-text-weight-semibold">
                        {{ props.option.entity.abbreviation }}
                      </span>
                      <span>{{ getCapacityTo(props) }}</span>
                      <div
                        v-if="props.option.core_protocol"
                        class="has-text-weight-semibold ml-5"
                      >
                        ({{ General.CORE_PROTOCOL.toUpperCase() }})
                      </div>
                    </div>
                  </template>
                  <template #selected="{ tags }">
                    <b-tag
                      v-for="(tag, index) in tags"
                      :key="index"
                      rounded
                      closable
                      :aria-close-label="
                        `${General.ARIA_LABEL.DELETE_FIELD} ${tag.name}`
                      "
                      :disabled="validatedStep2 || !completeLoad"
                      @close="
                        $refs.capacityTo.$children[0].removeTag(index, $event)
                      "
                    >
                      <div class="is-flex is-align-items-center">
                        <b-tooltip
                          :label="
                            isDestinationInternal(tag.entity.id)
                              ? General.INTERNAL_PROCESSING
                              : General.EXTERNAL_PROCESSING
                          "
                          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                          role="tooltip"
                          size="is-small"
                          position="is-top"
                          multilined
                        >
                          <b-icon
                            class="is-flex spacing-mx-5"
                            :icon="
                              isDestinationInternal(tag.entity.id)
                                ? 'alpha-i-circle'
                                : 'alpha-e-circle'
                            "
                            :type="
                              isDestinationInternal(tag.entity.id)
                                ? 'is-primary'
                                : 'is-warning'
                            "
                          />
                        </b-tooltip>
                        <b-tooltip
                          :label="tag.name"
                          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                          :size="isMobile ? 'is-small' : 'is-large'"
                          role="tooltip"
                          position="is-top"
                          multilined
                        >
                          <div class="tag-text is-flex">
                            <span class="has-text-weight-semibold">
                              {{ tag.entity.abbreviation }}
                            </span>
                            <span class="has-ellipsis">
                              {{
                                `/${
                                  tag.abbreviation.includes("/")
                                    ? tag.abbreviation.split("/")[1]
                                    : tag.abbreviation
                                } - ${tag.description}`
                              }}
                            </span>
                          </div>
                        </b-tooltip>
                      </div>
                    </b-tag>
                  </template>
                  <template slot="empty">
                    <p v-if="!isLoading">
                      {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
                    </p>
                    <b-loading v-else :isFullPage="false" :active="isLoading" />
                  </template>
                </CustomTagInput>
              </b-tooltip>
              <template #message>
                <ErrorField :errors="errors" />
              </template>
            </b-field>
          </ValidationProvider>

          <section class="columns">
            <div class="column">
              <div class="is-6 has-text-weight-semibold">
                <label>{{ General.ACCESS_LEVEL }}</label>
              </div>
              <b-tooltip
                :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                :size="isMobile ? 'is-small' : 'is-large'"
                :active="!newProcess.subject"
                class="tooltip-100"
                role="tooltip"
                position="is-top"
                multilined
              >
                <template v-slot:content>
                  <h6 class="has-text-weight-semibold is-flex">
                    {{ General.ALERT }}!
                  </h6>
                  <p class="has-text-left">
                    {{ App.ProcessOpeningScreen.SELECT_ACCESS_LEVEL }}
                  </p>
                </template>
                <b-dropdown
                  id="secrecy_subject"
                  v-model="secrecySubject"
                  data-cy="SECRECY_SUBJECT"
                  :mobileModal="false"
                  aria-role="list"
                  class="dropdown-field custom-disable is-full-child"
                  :disabled="
                    !newProcess.subject || !hasPermissionProcessSecrecy
                  "
                >
                  <b-button
                    slot="trigger"
                    slot-scope="{ active }"
                    class="subject-config-select"
                    type="is-secondary"
                    :iconRight="active ? 'chevron-up' : 'chevron-down'"
                    outlined
                  >
                    <span>{{ secrecySubject ? secrecySubject.name : "" }}</span>
                  </b-button>

                  <b-dropdown-item
                    v-for="(secrecy, index) in selectableSecrecies"
                    :key="index"
                    :value="secrecy"
                    aria-role="secrecyItem"
                    role="option"
                    @click="onSelectedSecrecy(secrecy)"
                  >
                    <span class="has-text-weight-semibold">{{
                      secrecy.name
                    }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </b-tooltip>
            </div>

            <div v-if="needsConfidentialPeriod" class="column">
              <div class="is-6 has-text-weight-semibold">
                <span class="subtitle is-6 has-text-weight-semibold">
                  {{ General.CONFIDENTIAL_PERIOD }}
                </span>
                <b-tooltip
                  multilined
                  role="tooltip"
                  :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                  :size="isMobile ? 'is-medium' : 'is-large'"
                  :position="isMobile ? 'is-top' : 'is-right'"
                >
                  <template v-slot:content>
                    <h6 class="has-text-weight-semibold is-flex">
                      {{ App.MenuMain.HELP }}
                    </h6>
                    <p class="has-text-left">
                      {{ General.TEXTS.CONFIDENTIAL_PERIOD }}
                    </p>
                  </template>
                  <div class="is-flex">
                    <b-icon
                      class="spacing-ml-10"
                      :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                      size="is-small"
                      icon="information"
                    />
                  </div>
                </b-tooltip>
              </div>

              <b-dropdown
                id="period_secrecy_subject"
                v-model="confidentialityPeriod"
                data-cy="PERIOD_CONFIDENTIAL"
                aria-role="list"
                scrollable
                maxHeight="200"
                class="dropdown-field custom-disable is-full-child"
                :disabled="!hasPermissionProcessSecrecy"
              >
                <b-button
                  slot="trigger"
                  slot-scope="{ active }"
                  class="subject-config-select"
                  type="is-secondary"
                  :iconRight="active ? 'chevron-up' : 'chevron-down'"
                  outlined
                >
                  <span>{{ confidentialityPeriod.description }}</span>
                </b-button>
                <b-dropdown-item
                  v-for="period in listPeriod"
                  :key="`period-${period.ref}`"
                  :value="period"
                  aria-role="priorityItem"
                  role="option"
                  @click="setSecrecyPeriod(period)"
                >
                  <span class="has-text-weight-semibold">{{
                    period.description
                  }}</span>
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <div class="column">
              <div>
                <label class="subtitle is-6 has-text-weight-semibold">{{
                  General.PRIORITY_LEVEL
                }}</label>
              </div>
              <b-dropdown
                id="priority_subject"
                v-model="prioritySubject"
                data-cy="PRIORITY_SUBJECT"
                :mobileModal="false"
                aria-role="list"
                class="dropdown-field custom-disable is-full-child"
                :disabled="!hasPermissionProcessPriority"
              >
                <b-button
                  slot="trigger"
                  slot-scope="{ active }"
                  class="subject-config-select"
                  type="is-secondary"
                  :iconRight="active ? 'chevron-up' : 'chevron-down'"
                  outlined
                >
                  <span>{{ prioritySubject.name }}</span>
                </b-button>
                <b-dropdown-item
                  v-for="(priority, index) in priorities"
                  :key="index"
                  :value="priority"
                  aria-role="priorityItem"
                  role="option"
                  @click="onSelectedPriority(priority)"
                >
                  <span class="has-text-weight-semibold">{{
                    priority.name
                  }}</span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <div
              v-if="newProcess.due_date"
              data-cy="DUE_DATE_BUTTON"
              class="column"
              @click.stop="showModalScheduler(true)"
            >
              <label class="is-6 has-text-weight-semibold">
                {{ General.DUE }}
              </label>
              <b-button
                class="due-date-hover"
                :class="{ 'has-text-danger': getDateDiff < 0 }"
              >
                <b-icon
                  icon="clock"
                  :type="isContrast ? 'is-white-dark' : 'is-warning'"
                  class="mx-5"
                />
                {{
                  new Date(newProcess.due_date.date) | dateFormat("DD/MM/YYYY")
                }}
                {{ `( ${getDateDiff} ${General.DAYS.toLowerCase()} )` }}
              </b-button>
            </div>
          </section>

          <div id="legal_reference" data-cy="LEGAL_REFERENCE" class="mb-15">
            <div class="is-6 has-text-weight-semibold">
              <label>{{ General.LEGAL_REFERENCE }}</label>
              <b-tooltip
                multilined
                role="tooltip"
                :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                :size="isMobile ? 'is-medium' : 'is-large'"
                :position="isMobile ? 'is-top' : 'is-right'"
              >
                <template #content>
                  <h6 class="has-text-weight-semibold is-flex">
                    {{ App.MenuMain.HELP }}
                  </h6>
                  <p class="has-text-left">
                    {{ General.TEXTS.DESCRIPTION_LEGAL_REFERENCE }}
                  </p>
                </template>
                <div class="is-flex">
                  <b-icon
                    class="spacing-ml-10"
                    :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                    size="is-small"
                    icon="information"
                  />
                </div>
              </b-tooltip>
            </div>
            <section class="legal-reference">
              <div class="list px-5 py-10">
                <b-tooltip
                  v-for="(tag, index) in subjectLegalReference"
                  :key="`fixed_reference_${index}`"
                  :label="tag.description"
                  :type="isContrast ? 'is-white' : 'is-blue-oil'"
                  position="is-top"
                  :size="isMobile ? 'is-small' : 'is-medium'"
                  :active="tag.description.length > 30"
                  multilined
                >
                  <b-tag
                    class="tag-field has-tag-ellipsis is-clickable mr-5"
                    rounded
                    closable
                    :tabstop="false"
                    :aria-close-label="
                      `${General.ARIA_LABEL.DELETE_TAG} ${tag.description}`
                    "
                    @close="showMessageSecrecy()"
                  >
                    <div class="tag-text is-flex">
                      <span class="has-ellipsis is-size-6">
                        {{ tag.description }}
                      </span>
                    </div>
                  </b-tag>
                </b-tooltip>
                <b-tooltip
                  v-for="(tag, index) in legal_reference"
                  :key="`reference_${index}`"
                  :label="tag.description"
                  :type="isContrast ? 'is-white' : 'is-blue-oil'"
                  position="is-top"
                  :size="isMobile ? 'is-small' : 'is-medium'"
                  :active="tag.description.length > 30"
                  multilined
                >
                  <b-tag
                    class="tag-field has-tag-ellipsis is-clickable mr-5 has-text-blue-oil tag-user-add"
                    rounded
                    :tabstop="false"
                    closable
                    type="is-white"
                    :aria-close-label="
                      `${General.ARIA_LABEL.DELETE_TAG} ${tag.description}`
                    "
                    @close="removeLegalReference(tag)"
                  >
                    <div class="tag-text is-flex">
                      <span class="has-ellipsis is-size-6">
                        {{ tag.description }}
                      </span>
                    </div>
                  </b-tag>
                </b-tooltip>
              </div>
              <span
                v-if="hasPermissionProcessSecrecy"
                data-cy="LEGAL_REFERENCE_BUTTON"
                class="is-outlined button is-fullwidth"
                role="button"
                @click="openModalLegalReference"
                >{{ General.CLICK_TO_ADD }}</span
              >
            </section>
          </div>

          <div id="stakeholder_to_field" data-cy="STAKEHOLDER_TO_FIELD">
            <div class="is-flex is-align-items-center">
              <label>
                <span class="is-6 has-text-weight-semibold">{{
                  General.INTERESTED
                }}</span>
                <span
                  v-if="!hasPermissionProtocoller"
                  class="optional-label ml-5"
                >
                  {{ General.OPTIONAL }}</span
                >
              </label>
              <b-tooltip
                multilined
                role="tooltip"
                :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                :size="isMobile ? 'is-medium' : 'is-large'"
                :position="isMobile ? 'is-top' : 'is-right'"
              >
                <template v-slot:content>
                  <h6 class="has-text-weight-semibold is-flex">
                    {{ App.MenuMain.HELP }}
                  </h6>
                  <p class="has-text-left">
                    {{ General.TEXTS.DESCRIPTION_INTERESTED }}
                  </p>
                </template>
                <div class="is-flex">
                  <b-icon
                    class="spacing-ml-10"
                    :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                    size="is-small"
                    icon="information"
                  />
                </div>
              </b-tooltip>
            </div>
            <div>
              <div class="stakeholder-field">
                <b-field
                  :label="General.PLACEHOLDER.SEARCH_IN"
                  labelPosition="on-border"
                  class="select-field"
                  customClass="has-text-weight-semibold"
                >
                  <b-select
                    v-model="typeSearchStakeholder"
                    class="stakeholder-select"
                    role="select"
                  >
                    <option
                      v-for="option in listTypeStakeholder"
                      :key="option[0]"
                      :value="option[0]"
                      role="option"
                    >
                      {{ option[1] }}</option
                    >
                  </b-select>
                </b-field>
                <b-tooltip
                  multilined
                  role="tooltip"
                  class="tooltip-100"
                  :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                  size="is-large"
                  :active="validatedStep2"
                  position="is-top"
                >
                  <template v-slot:content>
                    <h6 class="has-text-weight-semibold is-flex">
                      {{ App.MenuMain.HELP }}
                    </h6>
                    <p class="has-text-left">
                      {{ blockedFieldsTooltipText }}
                    </p>
                  </template>
                  <CustomTagInput
                    v-model="stakeholderTags"
                    data-cy="STAKEHOLDER_INPUT"
                    :beforeAdding="beforeAddingCC"
                    :data="stakeholderTagsData"
                    autocomplete
                    class="flex-1"
                    field="showCaseName"
                    name="stakeholder"
                    :placeholder="General.PLACEHOLDER.INTERESTED"
                    rounded
                    :disabled="validatedStep2"
                    role="combobox"
                    @typing="getStakeholders"
                    @input="onSelectedInterested"
                  >
                    <template slot="empty">
                      <p v-if="!isLoading">
                        {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
                      </p>
                      <b-loading
                        v-else
                        :isFullPage="false"
                        :active="isLoading"
                      />
                    </template>
                    <template slot-scope="props">
                      <span
                        v-if="props.option.id || props.option.type !== GUARDIAO"
                        >{{ props.option.name }}</span
                      >
                      <span v-if="props.option.cpf">
                        - {{ props.option.cpf }}
                      </span>
                    </template>
                  </CustomTagInput>
                </b-tooltip>
              </div>
            </div>
            <b-field>
              <template #label>
                <label>
                  <span class="is-6 has-text-weight-semibold">
                    {{ General.NOTES }}
                  </span>
                  <span class="optional-label ml-5">
                    {{ General.OPTIONAL }}
                  </span>
                </label>
              </template>
              <b-input
                type="textarea"
                data-cy="PROCESS_OBSERVATION_INPUT"
                :placeholder="General.PLACEHOLDER.TYPE_OBSERVATION"
                maxlength="100"
                rows="1"
                :value="newProcess.observation"
                @input="updateObservation"
              />
            </b-field>
          </div>
        </ValidationObserver>
      </TimelineItem>

      <TimelineItem
        :title="App.ProcessOpeningScreen.SOLICITATION"
        stepValue="2"
        :statusColor="`${!validatedStep2 ? 'default' : 'primary'}`"
      >
        <section class="container-form-section">
          <div class="columns is-desktop spacing-mt-10">
            <div class="column is-paddingless spacing-mx-10">
              <b-field v-if="!hasPermissionProtocoller">
                <template>
                  <div class="simple-row document-type-container">
                    <b-dropdown
                      id="initial_document"
                      v-model="initialDocumentTemplate"
                      :mobileModal="false"
                      scrollable
                      :maxHeight="200"
                      :disabled="
                        Boolean(initialFile(newProcess.id)) ||
                          !validatedStep1 ||
                          hierarchyFileTypes.length < 2
                      "
                      aria-role="list"
                      class="spacing-mr-20"
                      @input="selectInitialDocumentTemplate"
                    >
                      <b-button
                        slot="trigger"
                        slot-scope="{ active }"
                        class="document-type-select"
                        :type="isContrast ? 'is-white-dark' : 'is-secondary'"
                        :iconRight="active ? 'chevron-up' : 'chevron-down'"
                        outlined
                      >
                        <span>{{
                          getDocumentType(
                            initialFile(newProcess.id)
                              ? initialFile(newProcess.id).document_type
                              : null
                          ) ||
                            initialDocumentTemplate ||
                            General.SELECT_DOCUMENT_TYPE
                        }}</span>
                      </b-button>
                      <b-dropdown-item
                        v-for="(label, index) in hierarchyFileTypes"
                        :key="index"
                        :value="label ? label.type : ''"
                        aria-role="option"
                      >
                        <span class="has-text-weight-semibold">{{
                          label ? label.type : ""
                        }}</span>
                      </b-dropdown-item>
                    </b-dropdown>

                    <b-tooltip
                      multilined
                      role="tooltip"
                      :active="!processSubject || capacityTo.length === 0"
                      :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                      :position="isMobile ? 'is-top' : 'is-right'"
                      class="is-full"
                    >
                      <template v-slot:content>
                        <h6 class="has-text-weight-semibold is-flex">
                          {{ General.ALERT }}!
                        </h6>
                        <p class="has-text-left">
                          {{ General.MESSAGES.ALERT_CREATE_INITIAL_FILE }}.
                        </p>
                      </template>
                      <b-button
                        data-cy="INITIAL_FILE_BUTTON"
                        class="has-text-weight-semibold"
                        :disabled="disableCreateInitialFile"
                        :type="
                          Boolean(initialFile(newProcess.id))
                            ? initialFileButtonDisabledColor
                            : initialFileButtonEnabledColor
                        "
                        @click="documentEditor(undefined, true)"
                        >{{
                          !initialFile(newProcess.id)
                            ? General.CREATE
                            : App.ProcessOpeningScreen.SOLICITATION_CREATED
                        }}</b-button
                      >
                    </b-tooltip>
                  </div>
                </template>
              </b-field>
              <b-field v-else>
                <template>
                  <b-tooltip
                    multilined
                    role="tooltip"
                    :active="
                      Boolean(initialFile(newProcess.id)) || !validatedStep1
                    "
                    :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                    :size="isMobile ? 'is-small' : 'is-medium'"
                    :position="isMobile ? 'is-top' : 'is-right'"
                  >
                    <template v-slot:content>
                      <h6 class="has-text-weight-semibold is-flex">
                        {{ General.ALERT }}!
                      </h6>
                      <p class="has-text-left">
                        {{
                          General.MESSAGES.ALERT_CREATE_INITIAL_FILE_PROTOCOLER
                        }}
                      </p>
                    </template>

                    <b-button
                      data-cy="INITIAL_FILE_BUTTON_PROTOCOLLER"
                      class="spacing-mb-10"
                      :disabled="
                        Boolean(initialFile(newProcess.id)) || !validatedStep1
                      "
                      type="is-primary"
                      iconLeft="upload"
                      outlined
                      @click="showModalUpload(false, true)"
                      >{{ General.ATTACH_DOCUMENT }}
                    </b-button>
                  </b-tooltip>
                </template>
              </b-field>

              <DisplayAttachedDoc
                v-if="initialFile(newProcess.id)"
                :file="initialFile(newProcess.id)"
                :canSign="!hasPermissionProtocoller"
                :typeDocument="
                  getDocumentType(initialFile(newProcess.id).document_type)
                "
                initialDoc
                :showSignAlertModal="
                  !isSignatureDone && !hasPermissionProtocoller
                "
                :process="newProcess"
              />
            </div>
          </div>
        </section>
      </TimelineItem>

      <TimelineItem
        stepValue="3"
        :statusColor="
          `${!validatedStep1 || !validatedStep2 ? 'default' : 'primary'}`
        "
        hideProgressLine
      >
        <template #title>
          <h3 class="timeline-title is-size-5">
            {{ General.DOCUMENTS }}
            <span class="optional-label has-text-weight-normal is-size-5">{{
              General.OPTIONAL
            }}</span>
          </h3>
        </template>
        <section class="container-form-section">
          <b-collapse
            data-cy="FILE_NO_EDITABLE"
            class="collapse-custom"
            animation="slide"
            :open="Boolean(getModifiedProcessFiles.length)"
          >
            <template #trigger="props">
              <div
                class="is-flex is-align-items-center is-justify-content-space-between"
              >
                <label class="simple-row">
                  <div
                    id="collapse_doc_process"
                    class="is-6 spacing-mr-5 has-text-weight-semibold"
                  >
                    {{ General.PROCESS_DOCUMENTS }}
                  </div>
                  <div class="optional-label">
                    {{ General.OPTIONAL }}
                  </div>
                </label>
                <b-icon
                  size="is-medium"
                  :icon="props.open ? 'chevron-up' : 'chevron-down'"
                />
              </div>
            </template>
            <div class="columns is-desktop mb-10">
              <div class="column is-paddingless spacing-mx-10">
                <ListAttachments
                  v-model="currentPageNoEditable"
                  :onEdit="editFile"
                  :processNup="newProcess.nup"
                  :isSignatureDone="hasSignatureWithoutExternalDone"
                  :canSign="!hasPermissionProtocoller"
                  :documentEditor="documentEditor"
                  :showModalUpload="showModalUpload"
                  :process="newProcess"
                  tooltipOpenMobile="is-top"
                />
              </div>
            </div>
          </b-collapse>

          <b-collapse
            data-cy="FILE_EDITABLE"
            class="collapse-custom mt-20"
            animation="slide"
            :open="Boolean(getProcessFilesEditable.length)"
          >
            <template #trigger="props">
              <div
                class="is-flex is-align-items-center is-justify-content-space-between"
              >
                <label class="simple-row">
                  <div
                    id="collapse_doc_editable"
                    class="is-6 spacing-mr-5  has-text-weight-semibold"
                  >
                    {{ General.EDITABLE_FILES }}
                  </div>
                  <div class="optional-label">
                    {{ General.OPTIONAL }}
                  </div>
                  <b-tooltip
                    multilined
                    role="tooltip"
                    :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                    :size="isMobile ? 'is-medium' : 'is-large'"
                    :position="isMobile ? 'is-top' : 'is-right'"
                  >
                    <template v-slot:content>
                      <h6 class="has-text-weight-semibold is-flex">
                        {{ App.MenuMain.HELP }}
                      </h6>
                      <p class="has-text-left">
                        {{ General.TEXTS.TIP_EDITABLES_FILES }}
                      </p>
                    </template>
                    <div class="is-flex">
                      <b-icon
                        class="spacing-ml-10"
                        :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                        size="is-small"
                        icon="information"
                      />
                    </div>
                  </b-tooltip>
                </label>
                <b-icon
                  size="is-medium"
                  :icon="props.open ? 'chevron-up' : 'chevron-down'"
                />
              </div>
            </template>

            <div class="columns is-desktop mb-10">
              <div class="column is-paddingless spacing-mx-10">
                <ListAttachments
                  v-model="currentPageEditable"
                  :onEdit="editFile"
                  :processNup="newProcess.nup"
                  editables
                  :isSignatureDone="hasSignatureWithoutExternalDone"
                  :canSign="!hasPermissionProtocoller"
                  :editableDocument="editableDocument"
                  :showModalUpload="showModalUpload"
                  :process="newProcess"
                  tooltipOpenMobile="is-top"
                />
              </div>
            </div>
          </b-collapse>
          <div class="columns is-desktop my-10">
            <div class="column is-paddingless spacing-mx-10">
              <div>
                <b-checkbox
                  v-model="newProcess.user_notification.email"
                  data-cy="CHECK_NOTIFICATION_BUTTON"
                  :type="isContrast ? 'is-white-dark' : 'is-primary'"
                  class="checkbox-field spacing-mb-20"
                  size="is-medium"
                  role="checkbox"
                  @click.stop="checkNotification('email')"
                >
                  <span class="is-size-6 spacing-mr-15">
                    {{ General.TEXTS.RECEIVE_PROCESS_DATA }}
                  </span>
                </b-checkbox>
                <div class="is-flex">
                  <b-tooltip
                    :label="General.FAVOR"
                    role="tooltip"
                    :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
                  >
                    <div
                      class="button custom-background custom-button-border spacing-mr-20"
                      data-cy="FAVORITE_BUTTON"
                      role="button"
                      :aria-description="General.ARIA_LABEL.FAVORITE_BUTTON"
                      tabindex="0"
                      @click.stop="addFavorite"
                      @keyup.enter="addFavorite"
                    >
                      <b-icon
                        :icon="newProcess.favorite ? 'star' : 'star-outline'"
                        :type="
                          isContrast ? 'is-white-dark' : getFavoriteStarColor
                        "
                        size="is-small"
                      />
                    </div>
                  </b-tooltip>
                  <DropdownTagButton
                    :key="`bookmark_${newProcess.id}`"
                    :process="newProcess"
                    :bookmarkList="bookmarks"
                    @selectedBookmark="setBookmarks"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </TimelineItem>
      <div class="finish-buttons spacing-my-35 spacing-ml-50">
        <TramitingTooltip
          :hasMandatoryFilled="validatedStep1"
          :hasPrimaryFile="validatedStep2"
          :isInitialFileSigned="isInitialFileSigned"
          :hasSignatures="hasSignatures"
          :pendingSignatures="pendingSignaturesValue"
          :declinedSignatures="declinedSignaturesValue"
          :hasDOE="hasDOE"
          :hasDOEProgrammedSignatures="hasDOEProgrammedSignatures"
          :hasPermissionProtocoller="hasPermissionProtocoller"
          :position="isMobile ? 'is-top' : 'is-right'"
        >
          <b-button
            data-cy="SUBMIT_PROCESS_BUTTON"
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            :disabled="disableProcessing"
            role="button"
            @click="openModalSubmitProcess"
          >
            <span class="is-size-6">{{ General.PROCEED }}</span>
          </b-button>
        </TramitingTooltip>
        <TramitingTooltip
          :hasMandatoryFilled="validatedStep1"
          :hasPrimaryFile="validatedStep2"
          :hasSignatures="hasSignatures"
          :pendingSignatures="pendingSignaturesValue"
          :declinedSignatures="declinedSignaturesValue"
          :hasDOE="hasDOE"
          :hasDOEProgrammedSignatures="hasDOEProgrammedSignatures"
          :position="isMobile ? 'is-top' : 'is-right'"
          isAutomaticTramiting
        >
          <b-button
            data-cy="AUTOMATIC_PROCESS_BUTTON"
            :type="isContrast ? 'is-white-dark' : 'is-green-lawn'"
            outlined
            :disabled="disableAutomaticProcessing"
            class="custom-button-border"
            role="button"
            @click="openModalAutoProcess"
          >
            <span class="is-size-6">{{
              newProcess.automatic_processing
                ? General.CANCEL_AUTO_PROCESS
                : General.AUTO_PROCESS
            }}</span>
          </b-button>
        </TramitingTooltip>
        <b-button
          data-cy="SAVE_PROCESS_DRAFT_BUTTON"
          :type="isContrast ? 'is-white-dark' : 'is-green-lawn'"
          outlined
          class="custom-button-border"
          role="button"
          @click="confirmSaveDraft"
        >
          <span class="is-size-6">{{ General.SAVE_DRAFT }}</span>
        </b-button>
        <b-button
          data-cy="DELETE_PROCESS_DRAFT_BUTTON"
          :type="isContrast ? 'is-white-dark' : 'is-orange'"
          outlined
          class="custom-button-border"
          role="button"
          @click="confirmDelete"
        >
          <span class="is-size-6">{{ General.CANCEL }}</span>
        </b-button>
      </div>
    </template>
  </AppContent>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { v4 as uuidv4 } from "uuid";
import { debounce, truncate } from "lodash";
import { NUP, applyMask, getProtocolMask } from "@/utils/protocols";

import AppContent from "@/layout/AppContent";
import { getCCFormated } from "@/utils/transformers";

import ModalAttachments from "@/views/App/ModalAttachments";
import ModalScheduler from "@/views/App/ModalScheduler";

import TimelineItem from "@/components/TimelineItem";
import ListAttachments from "@/components/ListAttachments";
import DropdownTagButton from "@/components/DropdownTagButton";
import DisplayAttachedDoc from "@/components/DisplayAttachedDoc";
import ListSubmitProcess from "@/components/ListSubmitProcess";
import TramitingTooltip from "@/components/TramitingTooltip";

import { App, General } from "@/utils/constants";
import { getDateDiffDay } from "@/utils/timeUtility";
import {
  DOCUMENT_TYPE as DOC_TYPE,
  USER_PERMISSIONS as userPermissions,
  DOC_SIGNATURES_STATUS as DOC_SIGN,
  SIZES as sizes,
  STAKEHOLDER_TYPES as stakeholderTypes,
  PROCESS_FILES_MAX_SIZE,
  PRIORITY as priorities,
  SECRECY as secrecies,
  SECRECY_ID as secrecyId,
  ATTACHEMENT_MAX_COUNT,
  GUARDIAO
} from "@/utils/enumerate";
import ModalLegalReference from "@/views/App/ModalLegalReference.vue";
import ModalWarning from "@/views/App/ModalWarning.vue";
import ModalConfirmSubmit from "@/views/App/ModalConfirmSubmit.vue";
import ModalEditableDocument from "@/views/App/ModalEditableDocument.vue";

export default {
  name: "ProcessForm",

  components: {
    AppContent,
    TimelineItem,
    ListAttachments,
    DropdownTagButton,
    DisplayAttachedDoc,
    TramitingTooltip
  },

  data() {
    return {
      App,
      General,
      GUARDIAO,
      priorities,
      secrecies,
      userPermissions,
      DOC_SIGN,
      initialDocumentTemplate: null,
      initialDocumentTemplateId: null,
      dropdownActive: false,
      filterTagTo: "",
      isSelectOnly: false,
      stakeholderTags: [],
      stakeholderTagsData: [],
      capacityTo: [],
      validatedStep3: true,
      expanded: true,
      processSubject: "",
      confidentialityPeriod: { description: General.SELECT_LEVEL_PERIOD },
      secrecySubject: {},
      prioritySubject: {},
      selectProcess: "initial",
      referenceNumberDetails: null,
      dates: {
        start: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
        end: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000)
      },
      isLoading: true,
      filterLegalReference: "",
      legal_reference: [],
      process_reference: [],
      showDetailReferenceProcess: "",
      inPersonBidding: false,
      typeSearchStakeholder: "NATURAL_PERSON",
      currentPageNoEditable: 1,
      currentPageEditable: 1,
      getCCFormated,
      completeLoad: false,
      ignoreNewProcessUpdate: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      newProcess: "getNewProcess",
      bookmarks: "getBookmarks",
      subjects: "getSubjects",
      getSubjectById: "getSubjectById",
      hierarchyCapacities: "getHierarchyCapacitiesFiltered",
      editorFileTypes: "getFileTypesByEditor",
      getCapacitiesFiltered: "getCapacitiesFiltered",
      getModifiedProcessFiles: "getModifiedProcessFiles",
      getProcessFilesEditable: "getModifiedProcessFilesEditable",
      getFileTypeById: "getFileTypeById",
      initialFile: "getProcessInitialFile",
      getCapacityById: "getCapacityById",
      getProcessFiles: "getProcessFiles",
      processSignatureHistoryType: "getProcessSignatureHistoryType",
      socketGetDocumentToUpdate: "socketGetDocumentToUpdate",
      needUpdateAttachments: "needUpdateAttachments",
      holidays: "getHolidays",
      hasPermission: "hasPermission",
      isConfidentialCheck: "isConfidentialCheck",
      stakeHoldersUser: "getUsersFromSearch",
      getUpdatedDataStatus: "getUpdatedDataStatus",
      listProcess: "getListProcess",
      clearProcess: "getClearProcess",
      getProcessFileSize: "getProcessFileSize",
      isContrast: "isContrast",
      isMobile: "isMobile",
      mobileSize: "mobileSize",
      getListUploadFiles: "getListUploadFiles",
      getListSubmitProcess: "getListSubmitProcess",
      getFilteredLegalReferenceById: "getFilteredLegalReferenceById"
    }),

    needsConfidentialPeriod() {
      return (
        this.isConfidentialCheck(this.secrecySubject?.id) &&
        this.getSecreciesById(this.secrecySubject?.id)?.period
      );
    },

    listPeriod() {
      const maxPeriod = this.secrecySubject?.id
        ? this.getSecreciesById(this.secrecySubject.id).period
        : 0;

      return Array.from({ length: maxPeriod }, (_, index) => ({
        description: `${index + 1} ${index === 0 ? "ano" : "anos"}`,
        ref: index + 1
      }));
    },

    hasProcessSubmit() {
      return this.getListSubmitProcess.some(
        process =>
          !process.isErro && ["in progress", "pending"].includes(process.status)
      );
    },

    subjectLegalReference() {
      if (!this.newProcess.subject) return [];

      const secrecySubject = this.getSubjectById(this.newProcess.subject);
      if (!secrecySubject?.category) return [];
      const secrecyChanged =
        this.newProcess.secrecy !== secrecySubject.category.secrecy.id;
      if (secrecyChanged) return [];

      const references = secrecySubject.category.legal_reference ?? [];
      if (typeof references?.[0] !== "number") return references;
      return references.map(this.getFilteredLegalReferenceById);
    },

    listTypeStakeholder() {
      return Object.entries(stakeholderTypes);
    },

    processReferenceSelected() {
      if (this.showDetailReferenceProcess && this.process_reference.length)
        return this.process_reference.find(
          process => process.nup === this.showDetailReferenceProcess
        );
      return false;
    },

    changedLegalReference() {
      if (!this.newProcess.subject) return false;
      const secrecySubject = this.getSubjectById(this.newProcess.subject),
        isIncludeOnProcess = [
          secrecyId.RESTRICTED,
          secrecyId.CONFIDENTIAL,
          secrecyId.SECRET_CONFIDENTIAL,
          secrecyId.TOP_SECRET_CONFIDENTIAL
        ].includes(secrecySubject?.category?.secrecy?.id),
        changed =
          secrecySubject?.category?.secrecy?.id !== this.secrecySubject?.id;

      return isIncludeOnProcess && changed;
    },

    isOnlyInternalSolicitation() {
      return this.newProcess?.draft_to?.every(
        capacity => capacity.entityId === this.user.capacity.part_id
      );
    },

    isOnlyExternalSolicitation() {
      return this.newProcess?.draft_to?.every(
        capacity => capacity.entityId !== this.user.capacity.part_id
      );
    },

    currentUserIsStakeholder() {
      return this.stakeholderTags.some(
        stakeholder => stakeholder.user_id === this.user.id
      );
    },

    hierarchyFileTypes() {
      const internalTypes = this.editorFileTypes(2);
      const externalTypes = this.editorFileTypes(3);

      if (!this.newProcess || !this.newProcess.draft_to?.length) return [];

      const hasSingleDestination = this.newProcess.draft_to.length < 2;
      let types = [];

      if (this.isOnlyInternalSolicitation) {
        const typesFilter = hasSingleDestination
          ? [DOC_TYPE.CI]
          : [DOC_TYPE.CI_CIRCULAR];
        types = internalTypes.filter(type => typesFilter.includes(type.id));
      } else if (this.isOnlyExternalSolicitation) {
        const typesFilter = hasSingleDestination
          ? [DOC_TYPE.OFFICIO]
          : [DOC_TYPE.OFFICIO_CIRCULAR];
        types = externalTypes.filter(type => typesFilter.includes(type.id));
      } else
        types = externalTypes.filter(
          type => type.id == DOC_TYPE.OFFICIO_CIRCULAR
        );

      const hasRequirement = types.some(
        type => type.id === DOC_TYPE.REQUIREMENT
      );
      const isStakeholderSingleTag =
        this.currentUserIsStakeholder && this.stakeholderTags.length === 1;

      if (hasSingleDestination && !hasRequirement && isStakeholderSingleTag) {
        const requirementType = internalTypes.find(
          type => type.id === DOC_TYPE.REQUIREMENT
        );
        requirementType && types.push(requirementType);
      }

      return types;
    },

    formattedUserCapacity() {
      const capacityOrigin = this.getCapacityById(this.newProcess.origin);
      if (!capacityOrigin) return [];
      return [
        {
          showCaseName: `${capacityOrigin?.entity.abbreviation} - ${
            capacityOrigin?.abbreviation.includes("/")
              ? capacityOrigin?.abbreviation.split("/")[1]
              : capacityOrigin?.abbreviation
          }`,
          id: this.newProcess.origin
        }
      ];
    },

    validatedStep1() {
      if (!this.hasPermissionProtocoller)
        return (
          Boolean(this.newProcess.subject) &&
          Boolean(this.newProcess.draft_to.length)
        );
      else
        return Boolean(
          this.newProcess.subject &&
            this.newProcess.draft_to.length &&
            this.newProcess.cc.length
        );
    },

    validatedStep2() {
      if (this.hasPermissionProtocoller)
        return Boolean(this.initialFile(this.newProcess.id));
      return Boolean(this.newProcess && this.initialFile(this.newProcess.id));
    },

    getDateDiff() {
      const startDate = new Date(Date.now() + 1 * 24 * 60 * 60 * 1000);
      const endDate = new Date(this.newProcess.due_date.date);

      return getDateDiffDay(
        startDate,
        endDate,
        this.holidays,
        this.newProcess.due_date.is_weekdays
      );
    },

    disableCreateInitialFile() {
      return (
        Boolean(this.initialFile(this.newProcess.id)) ||
        !this.validatedStep1 ||
        !this.initialDocumentTemplateId
      );
    },

    isSignatureDone() {
      const initialFileProcess = this.initialFile(this.newProcess.id);
      return initialFileProcess?.sign_history?.some(
        history =>
          history.user_id !== this.user.id &&
          history.status.includes("SIGNED") &&
          !history.only_historical
      );
    },

    hasSignatureWithoutExternalDone() {
      const initialFileProcess = this.initialFile(this.newProcess.id);
      return initialFileProcess?.sign_history?.some(
        history =>
          history.user_id !== this.user.id &&
          history.status.includes("SIGNED") &&
          !history.is_external &&
          !history.only_historical
      );
    },

    disableProcessing() {
      return (
        this.newProcess.automatic_processing ||
        !this.validatedStep1 ||
        !this.validatedStep2 ||
        !(this.isInitialFileSigned || this.hasPermissionProtocoller) ||
        Boolean(this.pendingSignaturesValue) ||
        !this.hasDOEProgrammedSignatures ||
        Boolean(this.declinedSignaturesValue)
      );
    },

    disableAutomaticProcessing() {
      const hasPendingSignature = this.getProcessFiles.some(file =>
        [this.DOC_SIGN.PENDING, this.DOC_SIGN.PARTIALLY].includes(file.status)
      );

      return (
        (!hasPendingSignature ||
          !this.validatedStep1 ||
          !this.validatedStep2 ||
          Boolean(this.declinedSignaturesValue) ||
          (this.hasDOE && !this.hasDOEProgrammedSignatures)) &&
        !this.newProcess.automatic_processing
      );
    },

    isInitialFileSigned() {
      const initialFileProcess = this.initialFile(this.newProcess.id);
      return initialFileProcess?.can_tramit;
    },

    hasSignatures() {
      return this.getProcessFiles.some(file => file?.sign_history?.length);
    },

    pendingSignaturesValue() {
      return this.processSignatureHistoryType(
        this.getProcessFiles,
        this.DOC_SIGN.PENDING
      );
    },

    declinedSignaturesValue() {
      return this.processSignatureHistoryType(
        this.getProcessFiles,
        this.DOC_SIGN.REFUSED
      );
    },

    hasDOE() {
      return this.getProcessFilesEditable.some(file => file.to_publish);
    },

    hasDOEProgrammedSignatures() {
      return !this.getProcessFilesEditable.some(file =>
        file.to_publish ? !file.doe_obrigatory_signature.length : false
      );
    },

    hasPermissionProtocoller() {
      return this.hasPermission(userPermissions["PROTOCOLLER"]);
    },

    hasPermissionProcessSecrecy() {
      return this.hasPermission(userPermissions["PROCESS_SECRECY"]);
    },

    hasPermissionProcessPriority() {
      return this.hasPermission(userPermissions["PROCESS_PRIORITY"]);
    },

    initialFileButtonEnabledColor() {
      return this.isContrast ? "is-white-dark" : "is-primary";
    },

    initialFileButtonDisabledColor() {
      return this.isContrast ? "is-black" : "is-light";
    },

    getFavoriteStarColor() {
      return this.newProcess?.favorite ? "is-yellow" : "is-blue-dark";
    },

    blockedFieldsTooltipText() {
      return this.General.TEXTS.DESCRIPTION_TO.replace(
        "(TYPE)",
        this.App.ProcessOpeningScreen.SOLICITATION.toLowerCase()
      );
    },

    hasUploadFiles() {
      return this.getListUploadFiles.some(
        file =>
          file.process_id === this.newProcess.id &&
          !file.isErro &&
          file.status === "pending"
      );
    },

    selectableSecrecies() {
      return this.secrecies.filter(secrecy => secrecy.selectable);
    }
  },

  watch: {
    user: {
      handler() {
        this.initializeScreen();
      },
      deep: true
    },
    secrecySubject(processSecrecy) {
      const subject = this.getSubjectById(this.newProcess.subject);
      if (!subject) return;

      const period = this.getSecrecyPeriod(processSecrecy.id);
      if (!this.isLoading) this.updateConfidentialityPeriod(period);
    },
    hasProcessSubmit(status) {
      if (!status) this.cleaningProcess();
    },
    selectProcess: {
      handler() {
        this.$store.commit("setNewProcess", {
          ...this.newProcess,
          type: this.selectProcess
        });
        if (this.selectProcess === "reference") {
          setTimeout(
            () => this.$refs.processRef && this.$refs.processRef.focus(),
            200
          );
        }
      }
    },
    newProcess: {
      handler(oldValue, newValue) {
        if (this.ignoreNewProcessUpdate) {
          this.ignoreNewProcessUpdate = false;
          return;
        }
        !this.newProcess.id && this.initializeScreen();
        const changedSubject = oldValue.subject !== newValue.subject;
        if (!this.isLoading && this.newProcess.id)
          this.saveAutoDraft(changedSubject);
      },
      deep: true
    },
    clearProcess() {
      window.location.reload();
    },
    hierarchyFileTypes: {
      handler(types) {
        if (types.length === 1 && types[0]) {
          this.initialDocumentTemplate = types[0].type;
          this.initialDocumentTemplateId = types[0].id;
        }
      }
    },
    async getUpdatedDataStatus({ functionality }) {
      this.isLoading = true;
      if (functionality === "process_files") await this.loadProcessFile();
      else if (functionality === "process_files_editable")
        await this.loadProcessFileEditable();
      this.isLoading = false;
    },
    getProcessFileSize(value) {
      if (value > PROCESS_FILES_MAX_SIZE) {
        this.snackToast.open({
          message: this.General.TEXTS.LIMIT_PROCESS_SIZE_EXCEEDED,
          type: "is-danger",
          duration: 8000
        });
      }
    },
    async currentPageNoEditable() {
      this.isLoading = true;
      await this.loadProcessFile();
      this.isLoading = false;
    },
    async currentPageEditable() {
      this.isLoading = true;
      await this.loadProcessFileEditable();
      this.isLoading = false;
    },
    socketGetDocumentToUpdate: {
      async handler(value) {
        if (value.files_id?.length) {
          await this.requestProcessFiles(value);
          this.clearDocumentToUpdate();
        }
      },
      deep: true
    },
    needUpdateAttachments(value) {
      if (value.fileObject.process_id === this.newProcess.id)
        this.updateListAttachmentsAction(value);
    }
  },

  beforeDestroy() {
    this.removePrimaryFile(this.newProcess.id);
    window.removeEventListener(
      "beforeunload",
      this.verifyCloseSessionOnSubmitProcess
    );
    window.removeEventListener(
      "keydown",
      this.verifyCloseSessionOnSubmitProcess
    );
  },

  async beforeMount() {
    await this.initializeScreen();
    window.addEventListener(
      "beforeunload",
      this.verifyCloseSessionOnSubmitProcess
    );
    window.addEventListener("keydown", this.verifyCloseSessionOnSubmitProcess);
  },

  methods: {
    ...mapActions({
      submitDraft: "processSubmitDraftRequest",
      userFilter: "userFilterRequest",
      getCompanyByCNPJ: "getCompanyByCNPJ",
      requestSupplier: "supplierRequest",
      legalReferenceRequest: "legalReferenceRequest",
      subjectRequest: "subjectRequest",
      subjectRequestById: "subjectRequestById",
      setAutoProcessing: "setProcessAutoProcessing",
      searchProcessByNup: "searchProcessByNup",
      processFileRequest: "processFileRequest",
      fileTypesRequest: "fileTypesRequest",
      bookmarkRequest: "bookmarkRequest",
      requestProcessFiles: "processFilesFilteredRequest",
      capacityHierarchyRequest: "capacityHierarchyRequest",
      deleteProcessDraft: "deleteProcessDraft",
      processAutoDraftRequest: "processAutoDraftRequest",
      updateListAttachmentsAction: "updateListAttachmentsAction",
      requestProcessFileSize: "requestProcessFileSize",
      clearListSubmitProcess: "clearListSubmitProcess",
      checkDraftProcessSubmit: "checkDraftProcessSubmit",
      processSubmitTrigger: "processSubmitTrigger",
      getAllowedExtensions: "getAllowedExtensions"
    }),
    ...mapMutations([
      "removePrimaryFile",
      "clearDocumentToUpdate",
      "addProcessSubmitList",
      "updateProcessBookmark",
      "setNewProcess"
    ]),

    setSecrecyPeriod(period) {
      if (period?.ref) {
        this.snackToast.open({
          message: General.CONFIDENTIAL_PERIOD_UPDATED,
          position: "is-top",
          type: "is-success",
          duration: 8000
        });
      }
    },

    verifyCloseSessionOnSubmitProcess(event) {
      if (this.hasProcessSubmit) {
        const listenerActionsTaken =
          event.type === "keydown"
            ? (event.key === "r" && (event.ctrlKey || event.metaKey)) ||
              event.key === "F5"
            : event.type === "beforeunload";

        if (listenerActionsTaken) {
          event.preventDefault();
          this.snackToast.open({
            message: this.General.MESSAGES.WARNING_SUBMIT_PROCESS,
            type: "is-danger",
            duration: 8000
          });
        }
      }
    },

    isValidNup(event) {
      if (/[^\d-/.]/g.test(event.key)) {
        event.preventDefault();
      }
    },

    selectInitialDocumentTemplate(documentType) {
      const document = this.hierarchyFileTypes.find(
        models => models.type === documentType
      );
      this.initialDocumentTemplateId = document.id;
    },

    async loadProcessFile() {
      await this.processFileRequest({
        processId: this.newProcess.id,
        paginator_count: ATTACHEMENT_MAX_COUNT,
        page: this.currentPageNoEditable
      });
    },

    async loadProcessFileEditable() {
      await this.processFileRequest({
        processId: this.newProcess.id,
        editable: true,
        paginator_count: ATTACHEMENT_MAX_COUNT
      });
    },

    async initializeScreen() {
      this.isLoading = true;
      this.referenceNumberDetails = null;

      await this.processAutoDraftRequest();

      this.inPersonBidding = this.newProcess.in_person_bidding;

      const awaitRequests = [],
        asyncRequests = [
          () =>
            this.requestProcessFileSize({
              processId: this.newProcess.id,
              exclude_draft: false
            }),
          () => this.fileTypesRequest({ state: true }),
          () => this.bookmarkRequest({ all: false }),
          this.capacityHierarchyRequest,
          this.loadProcessFile,
          this.loadProcessFileEditable,
          this.getAllowedExtensions,
          this.legalReferenceRequest
        ];

      this.newProcess.subject &&
        awaitRequests.push(() =>
          this.subjectRequestById(this.newProcess.subject)
        );
      this.newProcess.reference_number &&
        awaitRequests.push(() =>
          this.getReferenceNumber(this.newProcess.reference_number)
        );

      Promise.allSettled(asyncRequests.map(fn => fn())).finally(() => {
        this.completeLoad = true;
      });
      await Promise.allSettled(awaitRequests.map(fn => fn()));

      this.prepareSubjectReturnSearch();
      this.prepareCapacityReturnSearch();

      if (this.newProcess.process_reference?.length) {
        this.process_reference = this.getFormattedProcessReferences(
          this.newProcess.process_reference
        );
      }
      if (this.newProcess.confidentiality_period) {
        this.updateConfidentialityPeriod(
          this.newProcess.confidentiality_period
        );
      }
      if (this.newProcess.automatic_processing_cancelled) {
        this.snackToast.open({
          message: this.General.MESSAGES.WARNING_AUTOMATIC_PROCESSING_CANCELLED,
          type: "is-danger",
          duration: 8000
        });
      }

      this.$nextTick(() => {
        this.isLoading = false;
      });
    },

    updateConfidentialityPeriod(period) {
      this.confidentialityPeriod = {
        description: `${period || 0} ${period === 1 ? "ano" : "anos"}`,
        ref: period
      };
      if (period !== this.newProcess.confidentiality_period)
        this.$store.commit("setNewProcess", {
          ...this.newProcess,
          confidentiality_period: period
        });
    },

    setLegalReference(text) {
      this.filterLegalReference = text;
    },

    setProcessReference: debounce(async function(search_text) {
      this.isLoading = true;
      const filterText = search_text.replace(/\D/g, "");
      filterText.length && (await this.searchProcessByNup(filterText));
      this.isLoading = false;
    }, 500),

    beforeAddingLegalReference(tag) {
      return !this.legal_reference.some(
        legalReference => legalReference.description === tag.description
      );
    },

    removeLegalReference(tag) {
      const newLegalReferenceList = this.legal_reference.filter(
        reference => reference.id !== tag.id
      );
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        legal_reference: newLegalReferenceList
      });
      this.legal_reference = newLegalReferenceList;
    },

    removeProcessReference(options, index) {
      this.process_reference = options.filter(
        (_, index_process) => index_process !== index
      );
      this.showDetailReferenceProcess =
        this.process_reference.length &&
        this.process_reference[this.process_reference.length - 1].nup;

      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        process_reference_list: this.process_reference.map(
          process => process.id
        )
      });
    },

    beforeAddingProcessReference(option) {
      return !this.process_reference.find(process => process.id === option.id);
    },

    applyMaskNUP(value) {
      return applyMask(NUP, value);
    },

    formatUserData(user) {
      return {
        ...user,
        user_id: user.id,
        user_name: user.name,
        user_cpf: user.cpf,
        showCaseName: truncate(`${user.name} - ${user.cpf}`, {
          length: 50
        })
      };
    },

    formatCompanyData(company) {
      return {
        cpf: company.cnpj,
        id: company.id || company.cnpj,
        name: company.name,
        user_id: null,
        user_name: company.name,
        user_cpf: company.cnpj,
        cnpj: company.cnpj,
        showCaseName: `${company.name} - ${company.cnpj?.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          "$1.$2.$3/$4-$5"
        )}`
      };
    },

    formatCapacityData(capacity) {
      return {
        ...capacity,
        showCaseName: truncate(capacity.name, { length: 50 }),
        capacity_id: capacity.id
      };
    },

    getStakeholders: debounce(async function(text) {
      const cleanText = text.replace(/[^A-ZÀ-Ú0-9\s]/gi, "").trim();

      if (cleanText === "") {
        this.stakeholderTagsData = [];
        return;
      }

      let formatedUsers = [];
      let formatedCompany = [];
      let formatedCapacity = [];

      this.isLoading = true;

      const searchByCPF = cleanText.replace(/\D/g, "");
      const isOnlyNumber = !isNaN(cleanText);
      const searhParam =
        searchByCPF.length === 11 ? { cpf: searchByCPF } : { name: cleanText };
      switch (this.typeSearchStakeholder) {
        case "NATURAL_PERSON":
          await this.userFilter({ ...searhParam, search_cc: true });
          formatedUsers = this.stakeHoldersUser
            .map(this.formatUserData)
            .sort((prev, next) => prev.name.localeCompare(next.name));
          break;
        case "LEGAL_ENTITY":
          if (isOnlyNumber && cleanText.length < 14) {
            this.isLoading = false;
            return;
          }

          formatedCompany = (
            await this.requestSupplier(
              /\d{14}/.test(cleanText) ? { cnpj: cleanText } : { name: text }
            )
          ).map(this.formatCompanyData);
          break;
        case "CAPACITY":
          formatedCapacity = this.getCapacitiesFiltered(text)
            .map(this.formatCapacityData)
            .sort((prev, next) => prev.name.localeCompare(next.name));
          break;
      }

      this.stakeholderTagsData = [
        ...formatedUsers,
        ...formatedCapacity,
        ...formatedCompany
      ];
      this.isLoading = false;
    }, 800),

    onDateSelect(value) {
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        due_date: {
          date: value.end,
          is_weekdays: value.isWeekdays
        }
      });
      this.dates = value;
    },

    showModalScheduler(canCancel = false) {
      this.$buefy.modal.open({
        parent: this,
        component: ModalScheduler,
        props: {
          dates: this.dates,
          onSelect: this.onDateSelect
        },
        canCancel: canCancel ? ["escape", "x", "outside"] : [],
        hasModalCard: true,
        trapFocus: true
      });
    },

    saveInitialDocument(title, description) {
      this.initialDocument = { id: 1, title: title + ".pdf", description };
    },

    async getReferenceNumber(referenceNumber) {
      this.referenceNumberDetails = await getProtocolMask(referenceNumber);
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        reference_number: this.referenceNumberDetails.valueMask
      });
    },

    async updateReference(ref) {
      if (ref.target.value.toString().trim() === "") {
        this.$store.commit("setNewProcess", {
          ...this.newProcess,
          reference_number: null
        });
        this.selectProcess = "initial";
        this.referenceNumberDetails = null;
      } else {
        this.selectProcess = "reference";
        await this.getReferenceNumber(ref.target.value);
        const newRefNumber = await getProtocolMask(ref.target.value);
        this.$store.commit("setNewProcess", {
          ...this.newProcess,
          reference_number: newRefNumber.valueMask
        });
      }
    },

    setBookmarks(selectedBookmarks) {
      const newBookmarks = selectedBookmarks;

      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        bookmark: newBookmarks
      });
    },

    setFilterTagTo(text) {
      this.isLoading = true;
      this.filterTagTo = text;
      this.isLoading = false;
    },

    async editFile(id, key, value, editable = false) {
      const editedFile = !editable
        ? this.getModifiedProcessFiles.find(file => file.id === id)
        : this.getProcessFilesEditable.find(file => file.id === id);

      if (editedFile[key] !== value) {
        editedFile[key] = value;
        await this.$store.dispatch("editProcessFile", {
          gdoc: editedFile,
          editable: editable
        });
      }
    },

    getSubjects: debounce(async function(search_text) {
      this.isLoading = true;
      await this.subjectRequest(search_text);
      this.isLoading = false;
    }, 500),

    onSelectedSubject(option = { id: null }, clear = false) {
      if (!option || !option.id || clear) {
        this.dates = {
          start: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
          end: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
        };

        this.secrecySubject = {};
        this.confidentialityPeriod = {
          description: General.SELECT_LEVEL_PERIOD
        };

        this.$store.commit("setNewProcess", {
          ...this.newProcess,
          due_date: null,
          subject: null,
          secrecy: null,
          priority: null,
          confidentiality_period: undefined
        });
      } else if (option.id !== this.newProcess.subject) {
        if (option.category.in_person_bidding) {
          this.$buefy.modal.open({
            parent: this,
            component: ModalWarning,
            props: {
              title: General.WARNING.toUpperCase(),
              message: App.ProcessOpeningScreen.WARNING_IN_PERSON_SUBJECT,
              confirm: () => {
                this.updateSubject(option);
              },
              deny: () => {
                this.processSubject = "";
              },
              buttons: [
                { label: General.AWARE, isConfirm: true, action: true },
                { label: General.CANCEL, isConfirm: false, action: false }
              ],
              invertOutline: true
            },
            hasModalCard: true,
            trapFocus: true,
            canCancel: []
          });
        } else {
          this.updateSubject(option);
        }
      }
    },

    updateSubject(subject) {
      const subjectCategory = subject.category;
      const secrecySelect = subjectCategory.secrecy;
      const prioritySelect = subjectCategory.priority;

      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        subject: subject.id,
        secrecy: secrecySelect.id,
        priority: prioritySelect.id,
        confidentiality_period: this.getSecrecyPeriod(secrecySelect.id)
      });

      this.prioritySubject = prioritySelect;
      this.secrecySubject = secrecySelect;

      if (prioritySelect.id === 2) this.showModalScheduler();
      else {
        this.$store.commit("setNewProcess", {
          ...this.newProcess,
          due_date: null
        });
        this.dates = {
          start: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
          end: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
        };
      }
    },

    getSecrecyPeriod(secrecyId) {
      const secrecy = this.getSecreciesById(secrecyId);
      if (!secrecy?.period) return undefined;
      const subject = this.getSubjectById(this.newProcess.subject);
      if (!subject) return undefined;

      const subjectCategorySecrecy = subject.category.secrecy;
      const subjectPeriod = subject.category.confidentiality_period;

      if (subjectPeriod && subjectCategorySecrecy.id === secrecyId) {
        return subjectPeriod;
      }

      return secrecy.period;
    },

    onSelectedSecrecy(secrecySelect) {
      if (secrecySelect.id != this.newProcess?.secrecy) {
        this.$store.commit("setNewProcess", {
          ...this.newProcess,
          secrecy: secrecySelect.id,
          legal_reference: [],
          confidentiality_period: this.getSecrecyPeriod(secrecySelect.id)
        });
        this.legal_reference = [];
      }
    },

    onSelectedPriority(option) {
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        priority: option.id
      });

      if (option.id === 2) this.showModalScheduler();
      else {
        this.$store.commit("setNewProcess", {
          ...this.newProcess,
          due_date: null
        });
        this.dates = {
          start: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
          end: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
        };
      }
    },

    confirmDelete() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: this.General.MESSAGES.ARE_YOU_SURE_YOU_WANT_CANCEL_DRAFT,
          confirm: () => {
            this.deleteProcessDraft([{ process_id: this.newProcess.id }]).then(
              this.cleaningProcess
            );
          },
          buttons: [
            {
              label: General.YES,
              isConfirm: true,
              action: true,
              dataCY: "CONFIRM_DELETE"
            },
            {
              label: General.NO,
              isConfirm: false,
              action: false,
              dataCY: "CANCEL_DELETE"
            }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    async confirmSaveDraft() {
      const result = await this.submitDraft(this.newProcess);
      if (result) {
        this.cleaningProcess(false);
      }
    },

    async openModalAutoProcess() {
      if (this.hasUploadFiles)
        return this.snackToast.open({
          message: General.TEXTS.WAIT_FOR_ALL_FILES_TO_LOAD,
          position: "is-top",
          type: "is-danger",
          duration: 8000
        });

      if (!this.newProcess.automatic_processing) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalConfirmSubmit,
          props: {
            process: this.newProcess,
            confirm: this.toogleAutomaticProcessing,
            isAutoSubmit: true
          },
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: `${this.General.MESSAGES.WARNING_CANCEL_AUTO_PROCESS}?`,
            confirm: this.toogleAutomaticProcessing,
            isMessageCentered: true
          },
          buttons: [
            { label: General.YES, isConfirm: true, action: true },
            { label: General.NO, isConfirm: false, action: false }
          ],
          hasModalCard: true,
          trapFocus: true
        });
      }
    },

    async toogleAutomaticProcessing() {
      this.isLoading = true;
      const result = await this.setAutoProcessing({
        processId: this.newProcess.id,
        automatic: !this.newProcess.automatic_processing
      });
      if (result) {
        await this.processAutoDraftRequest();
      }
      this.isLoading = false;
    },

    onSelectedToDraftAndLimitToProcessor(list) {
      if (this.hasPermissionProtocoller) {
        if (list.length > 1) this.capacityTo.shift();
      }
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        draft_to: list
      });
      this.initialDocumentTemplate = null;
      this.initialDocumentTemplateId = null;
    },

    onSelectedInterested(list) {
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        cc: list
      });
    },

    prepareCapacityReturnSearch() {
      this.capacityTo = [];

      this.newProcess?.draft_to?.length &&
        this.newProcess.draft_to.map(capacity => {
          this.capacityTo.push({
            ...capacity,
            name: `${capacity.entity.abbreviation}/${
              capacity.abbreviation.includes("/")
                ? capacity.abbreviation.split("/")[1]
                : capacity.abbreviation
            } - ${capacity?.description}`,
            showCaseName: truncate(
              `${capacity.entity.abbreviation}/${
                capacity.abbreviation.includes("/")
                  ? capacity.abbreviation.split("/")[1]
                  : capacity.abbreviation
              } - ${capacity?.description}`,
              { length: this.isMobile ? 30 : 50 }
            )
          });
        });

      this.stakeholderTags = [];
      this.newProcess?.cc?.length &&
        this.newProcess.cc.map(cc => {
          if (cc.user_cpf)
            this.stakeholderTags.push({
              ...cc,
              name: cc.name,
              showCaseName: `${cc.user_name} - ${cc.user_cpf}`
            });
          else
            this.stakeholderTags.push({
              ...cc,
              name: `${this.getCCFormated(cc)} - ${cc.description}`,
              showCaseName: truncate(
                `${this.getCCFormated(cc)} - ${cc.description}`,
                { length: 50 }
              )
            });
        });
    },

    getPrioritiesById(id) {
      return this.priorities.find(option => option.id === id);
    },

    getSecreciesById(id) {
      return this.secrecies.find(option => option.id === id);
    },

    prepareSubjectReturnSearch() {
      const getSubject = this.subjects.find(
        subject => this.newProcess.subject === subject.id
      );

      this.processSubject = getSubject
        ? getSubject.name
            .toLowerCase()
            .split(" - ")
            .map(partName => `${partName[0].toUpperCase()}${partName.slice(1)}`)
            .join(" - ")
        : "";

      if (this.newProcess.secrecy) {
        this.secrecySubject = this.getSecreciesById(this.newProcess.secrecy);
      } else {
        this.secrecySubject = this.getSubjectById(this.newProcess.subject)
          ? this.getSubjectById(this.newProcess.subject).category.secrecy
          : {};
      }

      if (this.newProcess.priority) {
        this.prioritySubject = this.getPrioritiesById(this.newProcess.priority);
      } else {
        this.prioritySubject = this.getSubjectById(this.newProcess.subject)
          ? this.getSubjectById(this.newProcess.subject).category.priority
          : {};
      }

      if (this.newProcess.legal_reference)
        this.legal_reference = this.newProcess.legal_reference;
    },

    async saveAutoDraft(changedSubject) {
      this.isLoading = true;
      const result = await this.$store.dispatch(
        "processSaveAutoDraftRequest",
        this.newProcess
      );

      if (result) {
        this.process_reference = this.getFormattedProcessReferences(
          result.process_reference
        );

        if (
          changedSubject &&
          (result.in_person_bidding || this.inPersonBidding)
        ) {
          this.loadProcessFile();
          this.ignoreNewProcessUpdate = true;
          this.updateProcessBookmark(result.bookmark);
        }
        this.inPersonBidding = result.in_person_bidding;
      }
      this.isLoading = false;
    },

    async cleaningProcess(clear = true) {
      this.isLoading = true;
      await this.$store.commit("setNewProcess", {
        ...this.newProcess,
        user_notification: { email: false, sms: false },
        bookmark: [],
        draft_to: [],
        cc: [],
        favorite: false,
        reference_number: null,
        process_reference: [],
        process_reference_list: [],
        subject: null,
        due_date: null,
        automatic_processing: false,
        date: new Date(),
        type: "initial",
        priority_id: null,
        secrecy_id: null,
        legal_reference: [],
        confidentiality_period: undefined,
        id: clear ? this.newProcess.id : null
      });
      this.secrecySubject = { name: General.SELECT_LEVEL };
      this.prioritySubject = { name: General.SELECT_PRIORITY };
      this.stakeholderTags = [];
      this.process_reference = [];
      this.capacityTo = [];
      this.legal_reference = [];
      this.processSubject = null;
      this.referenceNumberDetails = null;
      this.inPersonBidding = false;
      this.$refs.processFormObserver.reset();
      await this.processAutoDraftRequest();
      await Promise.allSettled([
        this.loadProcessFile(),
        this.loadProcessFileEditable()
      ]);
      this.isLoading = false;
    },

    beforeAddingCC(tag) {
      if (tag.user_cpf)
        if (tag.user_cpf.includes("*"))
          return !this.newProcess.cc.find(
            capacity => capacity.user_id === tag.user_id,
            false
          );
        else
          return !this.newProcess.cc.find(
            capacity => capacity.user_name === tag.user_name,
            false
          );
      return (
        tag.capacity_id !== this.user.capacity.capacity_id &&
        !this.newProcess.cc.find(
          capacity => capacity.capacity_id === tag.capacity_id,
          false
        )
      );
    },

    beforeAddingTo(tag) {
      return !this.newProcess.draft_to.find(
        capacity => capacity.capacity_id === tag.capacity_id,
        false
      );
    },

    async isObligatoryValidated() {
      return await this.$refs.processFormObserver.validate();
    },

    validateProcess() {
      const msg = [];
      if (!this.newProcess.subject) msg.push(this.General.SUBJECT);

      if (!this.newProcess.draft_to?.length) msg.push(this.General.TO);

      if (
        (this.getProcessFilesEditable.length &&
          this.getProcessFilesEditable.find(file => !file.attachment_name)) ||
        (this.getProcessFiles.length &&
          this.getProcessFiles.find(file => !file.attachment_name))
      )
        msg.push(this.General.MESSAGES.ALL_ATTACHMENT_NAME);

      if (!this.initialFile(this.newProcess.id))
        msg.push(this.App.ProcessPainelScreen.DESCRIPTION);

      if (this.hasPermissionProtocoller) {
        if (this.newProcess.cc?.length === 0)
          msg.push(this.General.MESSAGES.NECESSARY_TOFILL_INTHE_FIELDS);
      }

      if (msg.length) {
        this.snackToast.open({
          message: `${
            this.General.MESSAGES.NECESSARY_TOFILL_INTHE_FIELDS
          } ${msg.join(", ")}`,
          type: "is-danger",
          duration: 8000
        });
        return false;
      }
      return true;
    },

    async openModalSubmitProcess() {
      if (this.hasUploadFiles)
        return this.snackToast.open({
          message: General.TEXTS.WAIT_FOR_ALL_FILES_TO_LOAD,
          position: "is-top",
          type: "is-danger",
          duration: 8000
        });

      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        type: this.newProcess.reference_number ? "reference" : "initial"
      });

      if (this.validateProcess() || this.isObligatoryValidated()) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalConfirmSubmit,
          props: {
            process: this.newProcess,
            confirm: async () => await this.submitProcess()
          },
          hasModalCard: true,
          trapFocus: true
        });
      }
    },

    checkHasSubmitProcess() {
      const hasProcess = this.newProcess.draft_to.length > 1;
      if (hasProcess) {
        if (this.hasProcessSubmit) {
          const firstProcess = this.getListSubmitProcess[0].processBody;
          if (firstProcess.id !== this.newProcess.id)
            this.clearListSubmitProcess();
          else this.checkDraftProcessSubmit(firstProcess.id);
        }
        if (!this.hasProcessSubmit) {
          const processList = this.newProcess.draft_to.map(
            (draft_to, index) => ({
              processBody: {
                ...this.newProcess,
                draft_to: draft_to,
                delete_draft: index === this.newProcess.draft_to.length - 1
              },
              process_index: uuidv4(),
              percentage: 0,
              status: "pending"
            })
          );

          this.addProcessSubmitList(processList);
        } else {
          this.snackToast.open({
            message: General.TEXTS.PROCESS_SUBMIT_ALERT,
            position: "is-top",
            type: "is-danger",
            duration: 8000
          });
        }

        this.snackQueue.open({
          id: "process_submit",
          parent: this,
          component: ListSubmitProcess,
          closeAction: this.clearListSubmitProcess
        });

        this.processSubmitTrigger();
      }
      return this.hasProcessSubmit;
    },

    async submitProcess() {
      if (this.checkHasSubmitProcess()) return;
      this.isLoading = true;
      await this.$store.dispatch("processSubmitRequest", {
        ...this.newProcess,
        draft_to: this.newProcess.draft_to[0]
      });
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        id: null
      });
      this.process_reference = [];
      this.$refs.processFormObserver.reset();

      this.isLoading = false;
    },

    editableDocument(primary, fileType, editable = false) {
      if (this.isSignatureDone) {
        this.showAlertDocumentWithSignDone();
        return;
      }
      this.$buefy.modal.open({
        parent: this,
        component: ModalEditableDocument,
        props: {
          createDefault: () => this.documentEditor(fileType, primary, editable),
          processId: this.newProcess.id
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    documentEditor(fileType, primary = false, editable = false) {
      if (this.isSignatureDone) {
        this.showAlertDocumentWithSignDone();
        return;
      }
      const template =
        this.getFileTypeById(this.initialDocumentTemplateId) || {};

      this.$router.push({
        path: `/app/documento-editor/${
          this.newProcess.id
        }?template=${(fileType < 0 ? "" : fileType || template.id) || ""}`,
        query: { isPrimary: primary, isEditable: editable }
      });
    },

    showAlertDocumentWithSignDone() {
      this.snackToast.open({
        message: this.General.MESSAGES.WARNING_DOCUMENT_WITH_SIGN_DONE,
        type: "is-warning",
        duration: 8000
      });
    },

    checkNotification(key) {
      const newNotification = { ...this.newProcess.user_notification };

      newNotification[key] = !newNotification[key];

      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        user_notification: { ...newNotification }
      });
    },

    addFavorite() {
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        favorite: !this.newProcess.favorite
      });
    },

    showModalUpload(isEditable = false, isPrimary = false) {
      if (this.hasSignatureWithoutExternalDone) {
        this.showAlertDocumentWithSignDone();
        return;
      }
      this.$buefy.modal.open({
        parent: this,
        component: ModalAttachments,
        props: {
          editable: isEditable,
          isPrimary: isPrimary,
          currentSizeProcess: this.getProcessFileSize,
          currentProcessId: this.newProcess.id
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    getDocumentType(documentTypeId) {
      if (documentTypeId)
        return this.$store.getters.getFileTypeById(documentTypeId).type;
      return null;
    },

    openModalLegalReference() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalLegalReference,
        props: {
          list: this.legal_reference,
          listFromSubject: this.subjectLegalReference,
          change: newlist => {
            this.$store.commit("setNewProcess", {
              ...this.newProcess,
              legal_reference: [...newlist]
            });

            this.legal_reference = [...newlist];
          }
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    selectProcessReference(options) {
      this.process_reference = options;
      this.showDetailReferenceProcess = options[options.length - 1].nup;
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        process_reference_list: options.map(process => process.id)
      });
    },

    setDetailReferenceProcess(nup) {
      this.showDetailReferenceProcess = nup;
    },

    closeReferenceProcess() {
      this.showDetailReferenceProcess = "";
    },

    getCC(list) {
      return `${list
        .slice(0, 2)
        .map(
          cc =>
            `${cc.user_name || cc.abbreviation}${
              cc.user_cpf ? ` - ${cc.user_cpf}` : ` - ${cc.description}`
            }`
        )
        .join(", ")} ${list.length > 2 ? General.TEXTS.AND_OTHERS : ""}`;
    },

    isDestinationInternal(part) {
      return this.user.capacity.part_id === part;
    },

    getCapacityTo(props) {
      const truncateLength = this.mobileSize === sizes.IS_SMALL ? 15 : 30;
      if (this.isMobile && props.option.core_protocol)
        return truncate(
          `/${
            props.option.abbreviation.includes("/")
              ? props.option.abbreviation.split("/")[1]
              : props.option.abbreviation
          } - ${props.option.description}`,
          { length: truncateLength }
        );

      return `/${
        props.option.abbreviation.includes("/")
          ? props.option.abbreviation.split("/")[1]
          : props.option.abbreviation
      } - ${props.option.description}`;
    },

    getFormattedProcessReferences(referenceList) {
      return (
        referenceList?.map(reference => ({
          ...reference,
          origin: {
            ...this.getCapacityById(reference.origin)
          },
          cc: reference.cc.map(item => {
            const capacity = this.getCapacityById(item.capacity_id);
            return {
              ...item,
              abbreviation: capacity?.abbreviation,
              description: capacity?.description,
              entity: capacity?.entity
            };
          })
        })) ?? []
      );
    },

    updateObservation: debounce(function(observation) {
      this.$store.commit("setNewProcess", {
        ...this.newProcess,
        observation: observation || ""
      });
    }, 500),

    showMessageSecrecy() {
      this.snackToast.open({
        message: this.General.MESSAGES.ALERT_REMOVE_LEGAL_REFERENCE,
        type: "is-warning",
        duration: 8000
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.container-form-section {
  flex-grow: 1;
  position: relative;
}

.wrapper-field {
  align-content: space-between;
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex: 1;
}

.tooltip-100 {
  width: 100%;
}

.optional-label {
  font-size: 10px;
  font-weight: 300;
  color: $color-13;
}

.custom-button-border {
  border: 1px solid $color-8;
}

.custom-button-border:hover {
  opacity: 0.5;
}

.simple-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.document-type-container {
  display: flex;

  @media screen and (max-width: 768px) {
    height: 60px;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.document-type-select {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  text-align: left;

  @media screen and (min-width: 769px) {
    min-width: 270px;
  }
}

.subject-config-select {
  display: flex;
  min-width: 15vw;
  align-items: flex-start;
  justify-content: space-between;
  text-align: left;
}

.due-date-hover {
  display: flex;
  align-items: center;
  padding: 5px;
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  width: 100%;
  justify-content: flex-start;
  background-color: transparent;
  color: $color-7;
  cursor: pointer;
  &:hover {
    transition: 0.5s;
    background-color: $color-2;
  }
}

.p-custom {
  padding-left: 20px !important;
  padding-right: 2rem !important;
}
.py-9-custom {
  padding-top: 9px !important;
  padding-bottom: 9px !important;
}
.legal-reference {
  border: 1px solid $border-color;
  border-radius: 5px;
  .list {
    min-height: 60px;
  }
  .button {
    border: 0;
    border-top: 1px solid $border-color;
    color: $color-7;
    background-color: transparent;
    &:hover {
      background-color: $color-7;
      color: $color-2;
    }
  }
}
.finish-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media screen and (max-width: $tablet-low) {
    flex-direction: column;
    margin: 10px 10px 30px !important;
    .b-tooltip,
    .tooltip-trigger,
    button {
      width: 100%;
    }
  }
}

.tag-custom {
  background-color: $color-15;
  color: $color-7;
  border: 1px solid $custom-color-f2f2f2;
}

.custom-background {
  background-color: transparent;
}

.reference-number-tag {
  align-items: center;
  background-color: $color-20;
  border-radius: 4px;
  color: $blue-oil;
  display: inline-flex;
  font-size: 1rem;
  height: 2em;
  width: 100%;
  justify-content: flex-start;
  line-height: 1.5;
  padding: 15px;
  white-space: nowrap;
}

.tag-field {
  background-color: $color-15;
  color: $color-7;
  border: 1px solid $custom-color-f2f2f2;
}

.nup-tag {
  background-color: $color-19;
  padding: 15px;
  justify-content: flex-start;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
}

.stakeholder-field {
  display: flex;
  margin-top: 10px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/variables";
.taginput-custom > .taginput-container {
  min-width: 15vw;
}
.container-form-section {
  .checkbox-field {
    &:hover {
      color: var(--dynamic-title-color);
    }
  }

  .stakeholder-select {
    @media screen and (max-width: 768px) {
      .select,
      select {
        width: 100%;
      }
    }
  }

  .document-type-container {
    @media screen and (min-width: 768px) {
      width: min-content;
    }
    @media screen and (max-width: 768px) {
      .dropdown,
      .dropdown-trigger,
      button {
        width: 100%;
      }
    }
  }

  .dropdown-field.custom-disable {
    opacity: 1 !important;

    &.is-disabled button {
      background-color: $color-2;
      border: none;
    }
  }
}
</style>
